<template>
  <div>
    <b-container fluid="xxl" class="mt-2">
      <b-row class="mt-4">
        <b-col cols="4" class="p-0">
          <PageTitle v-bind:title="'Lemmas'" />
        </b-col>
        <b-col cols="8" class="text-right" v-if="!config.editable">
          <DictionarySelectPI class="mb-3" />
        </b-col>
      </b-row>
      <b-row class="mt-4" v-if="$store.getters.checkRole('legam.dict.article.add')">
        <b-col>
          <b-row>
            <b-col>
              <b-badge href="#" variant="info" class="mr-1" v-for="lemma in selectedLemmaWithoutArticles" :key="lemma.lemmaId" @click="removeSelectedLemma(lemma)">
                {{ lemmaDisplay(lemma) }}
                <b-icon icon="trash" aria-hidden="true" class="mr-1"></b-icon>
              </b-badge>
              <br>
              <b-button v-if="selectedLemmaWithoutArticles.length && config.editable" size="sm" variant="primary" class="mt-2" v-b-modal.modal-add-lemmas-to-article>
                <b-icon icon="plus-circle-fill" aria-hidden="true"></b-icon>
                Add lemmas to article
              </b-button>
              <b-modal v-if="config.editable" id="modal-add-lemmas-to-article" title="Add lemma to article" size="md">
                <b-badge href="#" variant="info" class="mr-1" v-for="lemma in selectedLemmaWithoutArticles" :key="'a' + lemma.lemmaId" @click="removeSelectedLemma(lemma)">
                  {{ lemmaDisplay(lemma) }}
                  <b-icon icon="trash" aria-hidden="true" class="mr-1"></b-icon>
                </b-badge>
                <ArticleAutocomplete v-if="$store.getters.checkRole('legam.dict.article.modify')" class="mt-4" :allowNew="false" @selectArticle="addLemmasToArticle" />
                <div class="ml-3 mt-2">
                  Dictionary<br>
                  <DictionarySelect @select="setDictionary" :multiple="false" />
                  <br>
                  <span v-for="lemma in selectedLemmaWithoutArticles" :key="lemma.lemmaId">
                    <a @click="newArticleFromLemma($event, lemma.lemmaId)" href="#">
                      <small>Create new article for</small> <b>{{ lemmaDisplay(lemma) }}</b>
                    </a>
                    <br>
                  </span>
                </div>
              </b-modal>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <p class="small text-muted mb-0">
        Regex fields support standard patterns, e.g. ^mot, mot$, or m.t.
      </p>
      <b-row class="mt-4">
        <b-col>
          <label for="lemma-search-input">Search all word forms</label>
          <b-form-input
            id="lemma-search-input"
            placeholder="Regex"
            v-model="filters.search"
            @keyup.enter="searchLemmata"
            size="sm"
          ></b-form-input>
        </b-col>
        <b-col>
          <label for="article-input">Article</label>
          <b-form-input
            id="article-input"
            placeholder="Regex"
            v-model="filters.articleLemma"
            @keyup.enter="searchLemmata"
            size="sm"
          ></b-form-input>
        </b-col>
        <b-col>
          <label for="mainLemma-input">Main Lemma</label>
          <b-form-input
            id="mainLemma-input"
            placeholder="Regex"
            v-model="filters.mainLemma"
            @keyup.enter="searchLemmata"
            size="sm"
          ></b-form-input>
        </b-col>
        <b-col>
          <label for="lemma-input">Lemma</label>
          <b-form-input
            id="lemma-input"
            placeholder="Regex"
            v-model="filters.lemma"
            @keyup.enter="searchLemmata"
            size="sm"
          ></b-form-input>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div class="pt-4">
            <b-button size="sm" variant="primary" class="mr-2" @click="searchLemmata">
              Search
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="resetSearch">
              Clear
            </b-button>
          </div>
        </b-col>
        <b-col>
          <label for="article-language-input">Language</label>
          <b-form-input
            id="article-language-input"
            placeholder="Regex"
            v-model="filters.articleLanguage"
            @keyup.enter="searchLemmata"
            size="sm"
          ></b-form-input>
        </b-col>
        <b-col>
          <label for="mainLemma-language-input">Language</label>
          <b-form-input
            id="mainLemma-language-input"
            placeholder="Regex"
            v-model="filters.mainLemmaLanguage"
            @keyup.enter="searchLemmata"
            size="sm"
          ></b-form-input>
        </b-col>
        <b-col>
          <label for="lemma-language-input">Language</label>
          <b-form-input
            id="lemma-language-input"
            placeholder="Regex"
            list="lemma-language-options"
            v-model="filters.lemmaLanguage"
            @keyup.enter="searchLemmata"
            size="sm"
          ></b-form-input>
          <datalist id="lemma-language-options">
            <option v-for="language in lemmaLanguageOptions" :key="language.code" :value="language.code">
              {{ language.name }}
            </option>
          </datalist>
        </b-col>
      </b-row>
      <b-row class="mt-4">
        <b-col>
          <div v-if="$store.state.user.settings.isDictionaryFiltered && config.editable" class="mb-3 text-orange">
            <b-icon icon="exclamation-circle-fill" aria-hidden="true"></b-icon>
            Entries in this table are filtered by personal preference for dictionary set in user settings
            ({{ $store.getters.getDictionariesByIds($store.state.user.settings.dictionaries).map(dictionary => dictionary.acronym).join(', ') }})
          </div>
          <b-button v-if="config.editable" variant="primary" size="sm" class="float-right" :disabled="!$store.getters.checkRole('legam.dict.lemma.add')"  v-b-modal.modal-new-lemma>
            <b-icon icon="plus-circle-fill" aria-hidden="true"></b-icon>
            Add new lemma
          </b-button>

          <b-modal v-if="config.editable" id="modal-new-lemma" title="New lemma" ok-title="Save" @ok="saveLemma" size="lg">
            <LemmaNewEdit :lemma="null" />
          </b-modal>
        </b-col>
      </b-row>
      <b-row class="mt-4">
        <b-col>
          <b-alert v-if="loadError" show variant="danger">
            {{ loadError }}
          </b-alert>
          <b-alert v-if="legacyWarning && !loadError" show variant="warning">
            The current export supports lemma-form matches only. Regenerate
            <code>lemma_fuzzy_search/</code> or <code>word_form_search/</code>
            for internal and external occurrence search.
          </b-alert>
          <div v-if="loading" class="text-center p-5">
            <b-spinner label="Loading lemmas"></b-spinner>
          </div>
          <b-table
            v-else-if="!loadError"
            class="table"
            ref="table"
            :fields="fields"
            :items="items"
            :fixed=true
            :hover=true
            :small=false
            :bordered=false
            :borderless=false
            :head-variant="headv"
            :tbody-tr-class="rowClass"
            :current-page="currentPage"
            :per-page="perPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-compare="sortCompare"
            show-empty
            empty-text="No matching lemmata"
            @row-clicked="onRowClicked"
            striped>
            <template #cell(articleLemma)="data">
              <a
                v-if="data.item.articleId"
                v-html="data.item.articleLemma"
                target="_blank"
                :href="`#/article/${data.item.articleId}`">
              </a>
            </template>
            <template #cell(mainLemma)="data">
              <a
                v-if="data.item.mainLemmaId"
                v-html="data.item.mainLemma"
                target="_blank"
                :href="`#/lemma/${data.item.mainLemmaId}`">
              </a>
            </template>
            <template #cell(lemma)="data">
              <a
                v-if="data.item.lemmaId"
                v-html="data.item.lemma"
                target="_blank"
                :href="`#/lemma/${data.item.lemmaId}`">
              </a>
            </template>
          </b-table>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="center"
            class="mt-3"
          ></b-pagination>
        </b-col>
      </b-row>
      <b-row class="mt-4">
        <b-col>
          <span>Number of Lemmata: {{ totalRows }}</span>
        </b-col>
        <b-col cols="3">
          <b-form-group label="Rows per page" label-for="lemmas-per-page">
            <b-form-select
              id="lemmas-per-page"
              v-model="perPage"
              :options="[25, 50, 100, 250]"
              size="sm"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </b-container>

    <div class="merge-float" v-if="selected.length && config.editable">
      <b-badge href="#" variant="info" class="mr-1" v-for="lemma in selected" :key="lemma.lemmaId" @click="removeSelectedLemma(lemma)">
        <span v-html="lemmaDisplay(lemma)"></span>
        <span v-if="lemma.articleId"> (<span v-html="lemma.articleLemma"></span>)</span>
        <b-icon icon="trash" aria-hidden="true" class="ml-1"></b-icon>
      </b-badge>
      <span class="btn-legam-xxs btn-legam-xs btn-legam-blue" v-if="selected.length > 1" v-b-modal.modal-merge-lemmas>
        <b-icon icon="plus-circle-fill" aria-hidden="true"></b-icon>
        Merge lemmas
      </span>
      <span class="btn-legam-xxs btn-legam-xs" @click="selected = []">
        <b-icon icon="x" aria-hidden="true"></b-icon>
      </span>
      <b-modal
        id="modal-merge-lemmas"
        title="Merge lemmas"
        ok-title="Merge"
        :ok-disabled="!mainLemma"
        @ok="mergeLemmas"
        size="md"
        >
        <b-badge href="#" variant="info" class="mr-1" v-for="lemma in selected" :key="'a' + lemma.lemmaId" @click="removeSelectedLemma(lemma)">
          <span v-html="lemmaDisplay(lemma)"></span>
          <span v-if="lemma.articleId"> (<span v-html="lemma.articleLemma"></span>)</span>
          <b-icon icon="trash" aria-hidden="true" class="ml-1"></b-icon>
        </b-badge>
        <b-form-group
          id="main-lemma-grp"
          label="Main lemma"
          label-for="main-lemma"
          class="mt-3"
          description="This lemma will keep all its data (other lemmas will move their occurrences to this lemma and then disappear)"
        >
          <b-form-select
            v-model="mainLemma"
            :options="selected.map(lemma => { return {
              'text': lemma.lemma + (lemma.articleId ? ' (' + lemma.articleLemma + ')' : ''),
              'value': lemma.lemmaId}
            })"
          ></b-form-select>
        </b-form-group>
      </b-modal>
    </div>
  </div>
</template>

<style>
  * >>> .tdAction {
    width: 100px;
  }
  .merge-float {
    position: fixed;
    bottom: 15px;
    left: 20px;
    background-color: #bebebe9a;
    padding: 3px 8px 5px;
    border-radius: 3px;
  }
  .highlighted-row {
    background-color: #d1ecf1 !important;
  }
  .b-table tbody tr {
    user-select: none; /* Prevents text selection */
  }
</style>

<script>
  import { mapState } from 'vuex'
  import PageTitle from '@/components/PageTitle.vue'
  import DictionarySelectPI from '@/components/Dictionary/DictionarySelectPI.vue'
  import LemmaNewEdit from '@/components/Lemma/NewEdit.vue'
  import ArticleAutocomplete from '@/components/ArticleAutocomplete.vue'
  import router from '@/router'
  import HelpStore from '@/store/helpstore.js'
  import DictionarySelect from '@/components/DictionarySelect.vue'
  import config from '@/config.js'
  import {
    compareLemmaRows,
    createLemmaSortKeyCache,
    dictionaryLanguageCodes,
    filterLemmaLanguageOptions,
    parseLemmaListRouteQuery,
    serializeLemmaListRouteQuery,
  } from '@/services/lemma-list.js'

  export default {
    name: 'LemmaList',
    data() {
      return {
        fields: [
          { key: 'articleLemma', label: 'Article', sortable: true },
          { key: 'articlePos', label: 'PoS', sortable: true },
          { key: 'articleLanguageCode', label: 'Lang.', sortable: true },
          { key: 'mainLemma', label: 'Main Lemma', sortable: true },
          { key: 'mainLemmaPos', label: 'PoS', sortable: true },
          { key: 'mainLemmaLanguageCode', label: 'Lang.', sortable: true },
          { key: 'lemma', label: 'Lemma', sortable: true },
          { key: 'lemmaPos', label: 'PoS', sortable: true },
          { key: 'lemmaLanguageCode', label: 'Lang.', sortable: true },
          { key: 'lemmaScriptae', label: 'Scriptae', sortable: true },
          { key: 'dictionary', label: 'Dictionary', sortable: true },
        ],
        items: [],
        url: '/lemmas/datatables',
        selected: [],
        selectedDictionary: null,
        key: 1,
        searchableByColumn: true,
        mainLemma: null,
        config: config,
        headv: 'light',
        filters: {
          search: '',
          articleLemma: '',
          articleLanguage: '',
          mainLemma: '',
          mainLemmaLanguage: '',
          lemma: '',
          lemmaLanguage: '',
        },
        currentPage: 1,
        perPage: 100,
        sortBy: 'lemma',
        sortDesc: false,
        totalRows: 0,
        loading: false,
        loadError: null,
        legacyWarning: false,
        initialized: false,
        routeSyncInProgress: false,
        applyingRouteQuery: false,
        lastRowClickedIndex: 0,
        collator: new Intl.Collator(undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
        sortKeyCache: createLemmaSortKeyCache(),
      }
    },
    components: {
      PageTitle,
      LemmaNewEdit,
      ArticleAutocomplete,
      DictionarySelect,
      DictionarySelectPI,
    },
    computed: {
      ...mapState({
        userSettings: state => state.user.settings,
        userSettingsPI: state => state.user.settingsPI,
        languages: state => state.common.languages,
        dictionaries: state => state.common.dictionaries,
      }),
      tableAdditionalData(){
        return this.userSettings.dictionaries
      },
      selectedLemmaWithoutArticles(){
        return this.selected.filter(lemma => lemma.articleId == null)
      },
      lemmaLanguageOptions() {
        return filterLemmaLanguageOptions(
          this.languages,
          this.dictionaries,
          this.userSettingsPI.dictionaries
        )
      }
    },
    watch: {
      "userSettings"(){
        this.key++
        if (this.initialized && !config.staticData) {
          this.searchLemmata()
        }
      },
      userSettingsPI: {
        deep: true,
        handler() {
          this.clearInvalidLemmaLanguage()
          if (this.initialized && config.staticData) {
            this.searchLemmata()
          }
        }
      },
      dictionaries() {
        this.clearInvalidLemmaLanguage()
      },
      currentPage() {
        if (this.initialized && !this.applyingRouteQuery) this.syncRouteQuery()
      },
      perPage() {
        if (!this.initialized || this.applyingRouteQuery) return
        this.currentPage = 1
        this.syncRouteQuery()
      },
      sortBy() {
        if (this.initialized && !this.applyingRouteQuery) this.syncRouteQuery()
      },
      sortDesc() {
        if (this.initialized && !this.applyingRouteQuery) this.syncRouteQuery()
      },
      '$route.query': {
        deep: true,
        handler(query) {
          if (!this.initialized || this.routeSyncInProgress) return
          this.applyRouteQuery(query)
          this.searchLemmata({ preservePage: true, syncRoute: false })
        },
      },
    },
    mounted() {
      if (config.staticData && !this.languages.length) {
        this.$store.dispatch('loadLanguages').catch(() => {})
      }
      this.applyRouteQuery(this.$route.query)
      this.searchLemmata({ preservePage: true, syncRoute: false })
        .finally(() => {
          this.initialized = true
          this.syncRouteQuery()
        })
    },
    methods: {
      lemmaDisplay(lemma) {
        if (lemma.lemmaLanguageCode && lemma.lemmaPos) {
          return `${lemma.lemmaLanguageCode} ${lemma.lemma} ${lemma.lemmaPos}`
        } else if (lemma.lemmaLanguageCode) {
          return `${lemma.lemmaLanguageCode} ${lemma.lemma}`
        } else if (lemma.lemmaPos) {
          return `${lemma.lemma} ${lemma.lemmaPos}`
        } else {
          return lemma.lemma
        }
      },
      onRowClicked(item, rowIndex, event) {
        if (!config.editable) return
        this.mainLemma = null

        if (event.shiftKey) {
          const factor = (this.currentPage - 1) * this.perPage
          this.items.slice(factor + this.lastRowClickedIndex, factor + rowIndex + 1).forEach(lemma => {
            const index = this.selected.findIndex(row => row.lemmaId === lemma.lemmaId)

            if (index < 0) {
              this.selected.push(lemma)
            }
          })

        } else {
          const index = this.selected.findIndex(row => row.lemmaId === item.lemmaId)

          if (index >= 0) {
            this.selected.splice(index, 1)
          } else {
            this.selected.push(item)
          }

          this.lastRowClickedIndex = rowIndex
        }
      },
      rowClass(item) {
        return this.selected.some(row => row.lemmaId === item.lemmaId) ? 'highlighted-row' : ''
      },
      async searchLemmata(options = {}){
        if (!options || typeof options !== 'object' || 'target' in options) {
          options = {}
        }
        const requestedPage = options.preservePage ? this.currentPage : 1
        this.loading = true
        this.loadError = null
        this.legacyWarning = false
        if (!options.preservePage) this.currentPage = 1
        try {
          const response = await this.$store.dispatch('lemmaTable', {
            ...this.filters,
            userDictionaries: this.tableAdditionalData,
            dictionaryAcronyms: this.userSettingsPI.dictionaries,
          })
          this.sortKeyCache.clear()
          this.items = response.data
          this.totalRows = response.count
          this.legacyWarning = Boolean(response.legacyFallback)
          const lastPage = Math.max(1, Math.ceil(this.totalRows / this.perPage))
          this.currentPage = Math.min(Math.max(1, requestedPage), lastPage)
          if (options.syncRoute !== false) this.syncRouteQuery()
        } catch (error) {
          this.sortKeyCache.clear()
          this.items = []
          this.totalRows = 0
          this.legacyWarning = false
          this.loadError = error.response && error.response.status === 404
            ? 'The static lemma search export is missing'
            : error.message || 'Lemmata could not be loaded'
        } finally {
          this.loading = false
        }
      },
      resetSearch() {
        Object.keys(this.filters).forEach(field => {
          this.filters[field] = ''
        })
        this.currentPage = 1
        this.perPage = 100
        this.sortBy = 'lemma'
        this.sortDesc = false
        this.searchLemmata()
      },
      clearInvalidLemmaLanguage() {
        const value = String(this.filters.lemmaLanguage || '').trim()
        if (!value || !this.userSettingsPI.dictionaries.length) return
        const allowed = dictionaryLanguageCodes(this.dictionaries, this.userSettingsPI.dictionaries)
        if (allowed && !allowed.has(value)) {
          this.filters.lemmaLanguage = ''
          this.currentPage = 1
          this.syncRouteQuery()
        }
      },
      applyRouteQuery(query) {
        this.applyingRouteQuery = true
        const state = parseLemmaListRouteQuery(
          query,
          this.fields.map(field => field.key)
        )
        this.filters = state.filters
        this.currentPage = state.currentPage
        this.perPage = state.perPage
        this.sortBy = state.sortBy
        this.sortDesc = state.sortDesc
        this.$nextTick(() => {
          this.applyingRouteQuery = false
        })
      },
      syncRouteQuery() {
        const query = serializeLemmaListRouteQuery(this)

        const current = this.$route.query
        const keys = new Set([...Object.keys(current), ...Object.keys(query)])
        if ([...keys].every(key => current[key] === query[key])) return

        this.routeSyncInProgress = true
        this.$router.replace({ name: 'Lemmas', query })
          .catch(() => {})
          .finally(() => {
            this.routeSyncInProgress = false
          })
      },
      sortCompare(rowA, rowB, key) {
        return compareLemmaRows(
          rowA,
          rowB,
          key,
          this.collator,
          this.sortKeyCache
        )
      },
      tableAdditionalDataFilter(data) {
        if (this.userSettingsPI.dictionaries.length) {
          data = data.filter(row => this.userSettingsPI.dictionaries.includes(row.dictionaryAcronym))
        }
        return data
      },
      mergeLemmas(){
        this.$store.dispatch('mergeLemmas', {
          mainLemmaId: this.mainLemma,
          lemmaIds: this.selected.map(lemma => lemma.lemmaId)
        }).then(() => {
          this.selected = []
          this.mainLemma = null
          this.key += 1
          this.searchLemmata()
        })
      },
      setDictionary(dictionary){
        this.selectedDictionary = dictionary[0]
      },
      selectChanged(items){
        this.selected = items
        if (items.length){
          this.mainLemma = items[0].id
        }
      },
      removeSelectedLemma(lemma){
        this.selected = this.selected.filter(_lemma => _lemma.lemmaId != lemma.lemmaId)
      },
      addLemmasToArticle(article){
        this.$store.dispatch('getLemmasWithEtymon', {
          lemmaIds: this.selectedLemmaWithoutArticles.map(lemma => lemma.lemmaId),
        }).then((response) => {
          const lemmasWithEtmyon = response.data.lemmas
          if (lemmasWithEtmyon.length > 0) {
            let text = ['From the following lemmata, their Etymons would be removed by this operation - continue?']

            const rows = lemmasWithEtmyon.map(row =>
              this.$createElement('tr', row.map(cell =>
                this.$createElement('td', cell.toString())
              ))
            )

            const header = this.$createElement('tr', [
              this.$createElement('th', 'Lemma'),
              this.$createElement('th', 'Etymon (will be removed)')
            ])

            const table = this.$createElement('table', { class: 'table table-bordered', style: 'font-size: smaller; margin-top: 20px;' }, [
              this.$createElement('thead', [header]),
              this.$createElement('tbody', rows)
            ])

            text.push(table)
            
            this.$bvModal.msgBoxConfirm(this.$createElement('div', text))
              .then(value => {
                if (value){
                  this.$store.dispatch('addLemmasToArticle', {
                    articleId: article.id,
                    lemmaIds: this.selectedLemmaWithoutArticles.map(lemma => lemma.lemmaId),
                  }).then((response) => {
                    this.$bvModal.hide('modal-add-lemmas-to-article')
                    router.push(`/article/${response.data.id}`)
                  })
                }
              })
          } else {
            this.$store.dispatch('addLemmasToArticle', {
              articleId: article.id,
              lemmaIds: this.selectedLemmaWithoutArticles.map(lemma => lemma.lemmaId),
            }).then((response) => {
              this.$bvModal.hide('modal-add-lemmas-to-article')
              router.push(`/article/${response.data.id}`)
            })
          }
        })
      },
      newArticleFromLemma(event, lemma){
        event.preventDefault()
        if (this.selectedDictionary){
          this.$store.dispatch('createArticle', {
            lemmas: this.selectedLemmaWithoutArticles.map(_lemma => _lemma.lemmaId),
            mainLemmaId: lemma,
            dictionaryId: this.selectedDictionary.id
          }).then((response) => {
            router.push(`/article/${response.data.id}`)
          })
        }
        else {
          alert('Please, select dictionary')
        }
      },
      saveLemma(){
        // this.$store.dispatch('createLemma', HelpStore.item)
        // this.$refs.DataTablesComponent.reloadTable()
        this.$store.dispatch('createLemma', HelpStore.item).then((response) => {
          router.push(`/lemma/${response.data.id}`)
        })
      }
    }
  }
</script>
